import { supabase } from './supabase'

export interface ServiceRow {
  id?: string
  slug: string
  name: string
  description: string | null
  duration_minutes: number | null
  base_price: number | string | null
  base_duration_minutes?: number | null
  price_cents?: number | null
  order_index?: number | null
  active?: boolean | null
  archived_at?: string | null
  created_at?: string
}

export interface BookingServiceCard {
  id: string
  name: string
  badge: string
  image: string
  description: string
  features: string[]
  price: string
  basePrice: number
  durationMinutes: number | null
}

export interface HomeServiceCard {
  id: string
  title: string
  copy: string
  image: string
  alt: string
  href: string
}

export interface HomePackageFeature {
  icon: 'clock' | 'droplet' | 'sun' | 'layers' | 'shield' | 'tool' | 'calendar' | 'diamond' | 'sparkle'
  label: string
}

export interface HomePackageCard {
  id: string
  slug: string
  eyebrow: string
  title: string
  subtitle?: string
  fromLabel: string
  price: string
  features: HomePackageFeature[]
  cta: string
  href: string
}

export interface EditableService {
  id?: string
  slug: string
  name: string
  description: string
  base_price: string
  duration_minutes: string
  order_index: number
  active: boolean
  archived_at?: string | null
}

const servicePresentation: Record<string, Omit<BookingServiceCard, 'price' | 'basePrice' | 'durationMinutes'>> = {
  'ceramic-coating': {
    id: 'ceramic-coating',
    name: 'Ceramic Coating',
    badge: 'PREMIUM PROTECTION',
    image: '/images/page-assets/booking-ceramic.jpg',
    description:
      'Our flagship protection service with multi-layer SiO2 coatings for a hydrophobic shield, intense depth, and chemical resistance for up to 5 years.',
    features: ['Self-cleaning hydrophobicity', 'UV and chemical resistance', 'GYEON ceramic protection'],
  },
  'paint-correction': {
    id: 'paint-correction',
    name: 'Paint Correction',
    badge: 'RESTORATION',
    image: '/images/page-assets/booking-paint-correction.jpg',
    description:
      'Restoring optical clarity by levelling clear coat imperfections and removing 85-99% of swirl marks, light scratches, and oxidation.',
    features: ['Multi-stage machine polishing', 'Swirl and hologram removal', 'Mirror-finish levelling'],
  },
  'signature-detail': {
    id: 'signature-detail',
    name: 'Signature Detail',
    badge: 'MAINTENANCE',
    image: '/images/page-assets/booking-signature-detail.jpg',
    description:
      'A comprehensive interior and exterior reset focused on every edge, crevice, surface, and touchpoint.',
    features: ['Steam decontamination', 'Clay bar treatment', 'Upholstery protection'],
  },
}

const fallbackPrices: Record<string, number> = {
  'ceramic-coating': 995,
  'paint-correction': 550,
  'signature-detail': 250,
  'basic-detail': 150,
  'full-detail': 450,
}

const fallbackDurations: Record<string, number> = {
  'ceramic-coating': 480,
  'paint-correction': 480,
  'signature-detail': 180,
  'basic-detail': 150,
  'full-detail': 480,
}

const homePackagePresentation: Record<string, Omit<HomePackageCard, 'id' | 'slug' | 'price'>> = {
  'basic-detail': {
    eyebrow: 'Entry Level',
    title: 'Basic Detail',
    fromLabel: 'From',
    features: [
      { icon: 'clock', label: '2-3 Hour Protection' },
      { icon: 'droplet', label: 'Snow Foam Wash' },
      { icon: 'sun', label: 'Enhanced Gloss' },
    ],
    cta: 'Book Basic',
    href: '/book',
  },
  'full-detail': {
    eyebrow: 'Certified Only',
    title: 'Full Detail',
    subtitle: 'The Studio Signature Service',
    fromLabel: 'From',
    features: [
      { icon: 'clock', label: 'Full Day Transformation' },
      { icon: 'layers', label: 'Multi-Stage Decon' },
      { icon: 'shield', label: 'Hydrophobic Sealant' },
      { icon: 'tool', label: 'Engine Bay Detail' },
    ],
    cta: 'Book Full Detail',
    href: '/book',
  },
  'ceramic-coating': {
    eyebrow: 'Full Defense',
    title: 'Ceramic Coating',
    fromLabel: 'From',
    features: [
      { icon: 'calendar', label: 'Warranty Eligible Packages' },
      { icon: 'diamond', label: 'Premium Ceramic Finish' },
      { icon: 'sparkle', label: 'Mirror Finish' },
    ],
    cta: 'Book Ceramic',
    href: '/ceramic-protection',
  },
  'paint-correction': {
    eyebrow: 'Restoration',
    title: 'Paint Correction',
    subtitle: 'Clarity Reset',
    fromLabel: 'From',
    features: [
      { icon: 'tool', label: 'Machine Polishing' },
      { icon: 'sparkle', label: 'Swirl Mark Removal' },
      { icon: 'sun', label: 'Showroom Gloss' },
    ],
    cta: 'Book Paint Correction',
    href: '/book',
  },
  'interior-detail': {
    eyebrow: 'Interior Care',
    title: 'Interior Detail',
    fromLabel: 'From',
    features: [
      { icon: 'droplet', label: 'Steam Cleaning' },
      { icon: 'sparkle', label: 'Deep Surface Reset' },
      { icon: 'shield', label: 'Interior Protection' },
    ],
    cta: 'Book Interior',
    href: '/book',
  },
  'exterior-detail': {
    eyebrow: 'Exterior Care',
    title: 'Exterior Detail',
    fromLabel: 'From',
    features: [
      { icon: 'droplet', label: 'Contact Wash' },
      { icon: 'layers', label: 'Paint Decontamination' },
      { icon: 'sun', label: 'Gloss Enhancement' },
    ],
    cta: 'Book Exterior',
    href: '/book',
  },
}

const fallbackHomePackageCards: HomePackageCard[] = [
  {
    id: 'basic-detail',
    slug: 'basic-detail',
    ...homePackagePresentation['basic-detail'],
    price: '$150',
  },
  {
    id: 'full-detail',
    slug: 'full-detail',
    ...homePackagePresentation['full-detail'],
    price: '$450',
  },
  {
    id: 'ceramic-coating',
    slug: 'ceramic-coating',
    ...homePackagePresentation['ceramic-coating'],
    price: '$850',
  },
]

const fallbackServices: ServiceRow[] = Object.keys(servicePresentation).map((slug, index) => ({
  slug,
  name: servicePresentation[slug].name,
  description: servicePresentation[slug].description,
  duration_minutes: fallbackDurations[slug],
  base_price: fallbackPrices[slug],
  order_index: index,
  active: true,
}))

const fallbackImages = [
  '/images/gyeon-protection.jpg',
  '/images/paint-correction.jpg',
  '/images/interior-detailing.jpg',
  '/images/detailing-packages.jpg',
]

export const currentServiceSlugs = Object.keys(servicePresentation)

export function formatAudCurrency(value: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value)
}

function toNumber(value: number | string | null | undefined, fallback: number) {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

function normalizeRows(rows: ServiceRow[] = []) {
  const activeRows = rows
    .filter((row) => row.active !== false && !row.archived_at)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))

  return activeRows.length > 0 ? activeRows : fallbackServices
}

function presentationFor(row: ServiceRow, index: number) {
  return servicePresentation[row.slug] ?? {
    id: row.slug,
    name: row.name,
    badge: 'AUTOGLOW SERVICE',
    image: fallbackImages[index % fallbackImages.length],
    description: row.description || 'Premium Autoglow detailing service tailored to the vehicle and finish condition.',
    features: ['Professional inspection', 'Premium detailing products', 'Studio-quality finish'],
  }
}

export function mergeBookingServices(rows: ServiceRow[] = []): BookingServiceCard[] {
  return normalizeRows(rows).map((row, index) => {
    const presentation = presentationFor(row, index)
    const basePrice = toNumber(row.base_price, row.price_cents ? row.price_cents / 100 : fallbackPrices[row.slug] ?? 0)

    return {
      ...presentation,
      id: row.id || row.slug,
      name: row?.name || presentation.name,
      description: row?.description || presentation.description,
      basePrice,
      price: formatAudCurrency(basePrice),
      durationMinutes: row?.duration_minutes ?? fallbackDurations[row.slug] ?? null,
    }
  })
}

export function mergeHomeServices(rows: ServiceRow[] = []): HomeServiceCard[] {
  return normalizeRows(rows).map((row, index) => {
    const presentation = presentationFor(row, index)

    return {
      id: row.id || row.slug,
      title: row.name,
      copy: row.description || presentation.description,
      image: presentation.image,
      alt: `${row.name} service by Autoglow Detailing.`,
      href: row.slug === 'ceramic-coating' ? '/ceramic-protection' : '/book',
    }
  })
}

function serviceDurationLabel(row: ServiceRow) {
  const duration = row.duration_minutes ?? row.base_duration_minutes ?? fallbackDurations[row.slug]

  if (!duration) return 'Professional Detail'
  if (duration < 60) return `${duration} Minute Detail`

  const hours = Math.round((duration / 60) * 10) / 10
  return `${hours % 1 === 0 ? Math.round(hours) : hours} Hour Detail`
}

function fallbackHomePackagePresentation(row: ServiceRow): Omit<HomePackageCard, 'id' | 'slug' | 'price'> {
  return {
    eyebrow: 'Autoglow Service',
    title: row.name,
    fromLabel: 'From',
    features: [
      { icon: 'clock', label: serviceDurationLabel(row) },
      { icon: 'tool', label: 'Professional Inspection' },
      { icon: 'sparkle', label: 'Studio-Quality Finish' },
    ],
    cta: `Book ${row.name}`,
    href: '/book',
  }
}

export function mergeHomePackageCards(rows: ServiceRow[] = []): HomePackageCard[] {
  if (rows.length === 0) return fallbackHomePackageCards

  return normalizeRows(rows).map((row) => {
    const presentation = homePackagePresentation[row.slug] ?? fallbackHomePackagePresentation(row)
    const basePrice = toNumber(row.base_price, row.price_cents ? row.price_cents / 100 : fallbackPrices[row.slug] ?? 0)

    return {
      ...presentation,
      id: row.id || row.slug,
      slug: row.slug,
      title: row.name || presentation.title,
      price: formatAudCurrency(basePrice),
    }
  })
}

export function mergeEditableServices(rows: ServiceRow[] = []): EditableService[] {
  const editableRows = rows.length > 0
    ? [...rows].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    : fallbackServices

  return editableRows.map((row, index) => {
    const basePrice = toNumber(row.base_price, row.price_cents ? row.price_cents / 100 : fallbackPrices[row.slug] ?? 0)

    return {
      id: row.id,
      slug: row.slug,
      name: row.name,
      description: row.description || '',
      base_price: String(basePrice),
      duration_minutes: String(row.duration_minutes ?? row.base_duration_minutes ?? fallbackDurations[row.slug] ?? 60),
      order_index: row?.order_index ?? index,
      active: row.active !== false,
      archived_at: row.archived_at ?? null,
    }
  })
}

const tailoredServiceSlugs = new Set([
  'paint-correction',
  'signature-detail',
  'interior-detail',
  'exterior-detail',
])

export function classifyAdminServiceGroup(service: Pick<EditableService, 'slug' | 'active' | 'archived_at'>): 'tailored' | 'legacy' {
  if (service.archived_at || !service.active) return 'legacy'
  if (tailoredServiceSlugs.has(service.slug)) return 'tailored'
  return 'legacy'
}

export async function fetchServiceRows(options: { includeInactive?: boolean; includeArchived?: boolean } = {}) {
  let query = supabase
    .from('services')
    .select('id, slug, name, description, duration_minutes, base_price, base_duration_minutes, price_cents, order_index, active, archived_at, created_at')
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true })

  if (!options.includeArchived) {
    query = query.is('archived_at', null)
  }

  if (!options.includeInactive) {
    query = query.neq('active', false)
  }

  const { data, error } = await query

  if (error) throw error
  return (data ?? []) as ServiceRow[]
}
